import * as React from 'react';
import { Column, ColumnInstance, SortingRule, UseSortByColumnProps, useSortBy, useTable } from 'react-table';
import { Icon, IconType } from '../icons';
import classNames from 'classnames';

export enum SortDirection {
  ascending = 'TableArrowUp',
  descending = 'TableArrowDown',
}
export interface IDataTableProps {
  arrowColour?: string;
  arrowHeight?: number;
  arrowWidth?: number;
  columnTitle?: string;
  columns: Column<Record<string, unknown>>[];
  data: Record<string, unknown>[];
  hiddenColumns?: string[];
  columnsWithSpacesPreserved?: string[];
  sortBy: SortingRule<Record<string, unknown>>[];
  updateData?(): void;
}

interface ISortArrowProps {
  arrowIconColour: string;
  arrowIconHeight: number;
  arrowIconWidth: number;
  sortDirection: SortDirection;
}

const SortArrows = (props: ISortArrowProps) => {
  return (
    <Icon
      height={props.arrowIconHeight}
      icon={IconType[props.sortDirection]}
      fill={props.arrowIconColour ? props.arrowIconColour : 'var(--color-black)'}
      width={props.arrowIconWidth}
    />
  );
};

export const DataTable = (props: IDataTableProps) => {
  const {
    arrowColour = 'var(--color-black)',
    arrowHeight = 8,
    arrowWidth = 20,
    columnTitle = 'Sorter etter',
    columnsWithSpacesPreserved = [],
  } = props;
  const columns = React.useMemo(() => [...props.columns], [props.columns]);
  const sortBy = [...props.sortBy];

  const Table: React.FC<IDataTableProps> = ({ columns, data, updateData }) => {
    const initialState: any = { sortBy };
    if (props.hiddenColumns) {
      initialState['hiddenColumns'] = props.hiddenColumns;
    }
    const useTableParams = { columns, data, initialState: initialState, updateData };
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
      {
        ...useTableParams,
      },
      useSortBy
    );

    const emptyDataTable = (
      <div className="w-full">
        <div className="w-full text-center">Tom liste</div>
      </div>
    );

    const table = (
      <table {...(getTableProps() as any)} className="m-0 w-[150%] border-none border-spacing-0">
        <thead className="h-[98px]">
          {headerGroups.map((headerGroup) => (
            <tr
              className="[&>*:nth-child(1)]:w-[6%] [&>*:nth-child(2)]:w-[6%] [&>*:nth-child(3)]:w-[6%]"
              {...(headerGroup.getHeaderGroupProps() as any)}
              key={headerGroup?.getHeaderGroupProps()?.key}
            >
              {headerGroup.headers.map((column) => {
                const sortableColumn = column as unknown as ColumnInstance<Record<string, unknown>> &
                  UseSortByColumnProps<Record<string, unknown>>;
                return (
                  <th
                    {...sortableColumn.getHeaderProps(
                      sortableColumn.getSortByToggleProps({
                        title: sortableColumn.canSort ? columnTitle : undefined,
                      })
                    )}
                    key={sortableColumn?.getHeaderProps()?.key}
                    className="p-[0.5rem] text-left break-words first:z-[100] border-none"
                    style={{ background: 'var(--color-very-light-grey)' }}
                  >
                    <span className="inline-flex items-center">
                      {sortableColumn.render('Header')}
                      <span className="inline-block">
                        {sortableColumn.isSorted ? (
                          sortableColumn.isSortedDesc ? (
                            <SortArrows
                              arrowIconColour={arrowColour}
                              arrowIconHeight={arrowHeight}
                              arrowIconWidth={arrowWidth}
                              sortDirection={SortDirection.descending}
                            />
                          ) : (
                            <SortArrows
                              arrowIconColour={arrowColour}
                              arrowIconHeight={arrowHeight}
                              arrowIconWidth={arrowWidth}
                              sortDirection={SortDirection.ascending}
                            />
                          )
                        ) : (
                          ''
                        )}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...(getTableBodyProps() as any)}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <tr {...(row.getRowProps() as any)} key={row?.getRowProps()?.key}>
                {row.cells.map((cell, cellIndex) => {
                  const preserveSpaces = columnsWithSpacesPreserved?.includes(cell.column.id);
                  return (
                    <td
                      {...(cell.getCellProps() as any)}
                      key={cell?.getCellProps()?.key}
                      className={classNames(
                        'relative py-[0.25rem] px-[0.313rem] text-left h-10 break-words',
                        '[&>input]:w-full',
                        '[&>input]:rounded-md',
                        '[&>input]:border',
                        '[&>input]:border-solid',
                        '[&>input]:border-black',
                        '[&>input]:font-normal',
                        '[&>input]:text-black',
                        '[&>input]:py-[0.4rem]',
                        '[&>input]:px-[0.3rem]',
                        '[&>input]:pr-[25px]',
                        '[&>span]:absolute',
                        '[&>span]:top-[0px]',
                        '[&>span]:bottom-[0px]',
                        '[&>span]:right-[0.7rem]',
                        '[&>span]:flex',
                        '[&>span]:items-center',
                        preserveSpaces ? 'whitespace-pre-wrap' : '',
                        'border-none p-[0.5rem] text-left height-[2.5rem]',
                        (cellIndex + 1) % 2 === 0 && (rowIndex + 1) % 2 === 0 ? 'bg-medium-grey' : undefined,
                        (cellIndex + 1) % 2 === 0 && (rowIndex + 1) % 2 !== 0 ? 'bg-lightest-grey' : undefined,
                        (cellIndex + 1) % 2 !== 0 && (rowIndex + 1) % 2 === 0 ? 'bg-lightGrey' : undefined
                      )}
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );

    return (
      <div className="relative w-full z-[1] mx-auto overflow-auto">
        {props.data.length === 0 ? emptyDataTable : table}
      </div>
    );
  };

  return <Table columns={columns} data={props.data} updateData={props.updateData} sortBy={props.sortBy} />;
};
